<template>
  <div class="page">
    <!-- HEADER -->
    <div class="page-head">
      <div>
        <h1>Users</h1>
      </div>

      <div class="actions">
        <div class="search-mini">
          <i class="bx bx-search"></i>
          <input v-model="query" type="text" placeholder="Search name, email, phone..." />
        </div>

        <select v-model="statusFilter" class="select">
          <option value="">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="SUSPENDED">Suspended</option>
        </select>

        <select v-model="kycFilter" class="select">
          <option value="">All KYC</option>
          <option value="VERIFIED">Verified</option>
          <option value="PENDING">Pending</option>
          <option value="REJECTED">Rejected</option>
          <option value="NOT_STARTED">Not Started</option>
        </select>

        <select v-model="planFilter" class="select">
          <option value="">All Plans</option>
          <option v-for="p in plans" :key="p.value" :value="p.value">{{ p.label }}</option>
        </select>

        <button class="btn ghost" @click="exportNow">
          <i class="bx bx-export"></i>
          Export
        </button>
      </div>
    </div>

    <!-- STATS -->
    <div class="grid cols-4">
      <div class="card stat">
        <div class="card-title">
          <strong>Total Users</strong>
          <span class="badge primary">LIVE</span>
        </div>
        <div class="metric">{{ users.length }}</div>
        <div class="mini">All registered accounts</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>KYC Pending</strong>
          <span class="badge warning">ACTION</span>
        </div>
        <div class="metric">{{ kycPending }}</div>
        <div class="mini">Need review/verification</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>Suspended</strong>
          <span class="badge warning">BLOCK</span>
        </div>
        <div class="metric">{{ suspended }}</div>
        <div class="mini">Login blocked</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>Active Today</strong>
          <span class="badge success">OK</span>
        </div>
        <div class="metric">{{ activeToday }}</div>
        <div class="mini">Recent app sessions</div>
      </div>
    </div>

    <!-- TABLE -->
    <div class="card" style="margin-top: 14px;">
      <div class="table-head">
        <div class="table-title">
          <strong>User List</strong>
          <span class="muted">Showing {{ filtered.length }} records</span>
        </div>

        <button class="btn primary" @click="openAddUserModal">
          <i class="bx bx-user-plus"></i>
          Add User
        </button>
      </div>

      <!-- Desktop -->
      <div class="table-wrap desktop-only">
        <table class="table">
          <thead>
            <tr>
              <th>USER</th>
              <th>PLAN</th>
              <th>KYC</th>
              <th>STATUS</th>
              <th>LAST ACTIVITY</th>
              <th style="text-align:right;">ACTIONS</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="u in filtered" :key="u.id">
              <td>
                <div class="usr">
                  <div class="avatar">{{ u.name.charAt(0).toUpperCase() }}</div>
                  <div class="usr-meta">
                    <div class="usr-name">{{ u.name }}</div>
                    <div class="row-muted">{{ u.email }} • {{ u.phone }}</div>
                  </div>
                </div>
              </td>

              <td>
                <span class="plan-pill">
                  <i class="bx bx-crown"></i>
                  {{ planLabel(u.plan) }}
                </span>
              </td>

              <td>
                <span class="status-pill" :class="kycClass(u.kyc)">
                  <i class="bx" :class="kycIcon(u.kyc)"></i>
                  {{ kycLabel(u.kyc) }}
                </span>
              </td>

              <td>
                <span class="status-pill" :class="u.status === 'ACTIVE' ? 'success' : 'danger'">
                  <i class="bx" :class="u.status === 'ACTIVE' ? 'bx-check-circle' : 'bx-block'"></i>
                  {{ u.status === 'ACTIVE' ? "Active" : "Suspended" }}
                </span>
              </td>

              <td class="row-muted">
                {{ u.lastActivity }}
              </td>

              <td style="text-align:right;">
                <div class="row-actions">
                  <button class="btn ghost" @click="openContacts(u.id)">
                    <i class="bx bx-phone"></i> Contacts
                  </button>
                  <button class="icon-mini" @click="openDrawer(u)" title="View Profile">
                    <i class="bx bx-show"></i>
                  </button>

                  <button class="icon-mini" @click="toggleStatus(u)" :title="u.status==='ACTIVE'?'Suspend':'Activate'">
                    <i class="bx" :class="u.status==='ACTIVE'?'bx-block':'bx-check-shield'"></i>
                  </button>

                  <button class="icon-mini danger" @click="remove(u)" title="Delete">
                    <i class="bx bx-trash"></i>
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="filtered.length === 0">
              <td colspan="6" style="padding: 18px;">
                <div class="empty">
                  <i class="bx bx-user-x"></i>
                  <div>
                    <strong>No users found</strong>
                    <div class="row-muted">Try adjusting filters or search query.</div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Cards -->
      <div class="mobile-only list-cards">
        <div v-for="u in filtered" :key="u.id" class="usr-card">
          <div class="top">
            <div class="usr">
              <div class="avatar">{{ u.name.charAt(0).toUpperCase() }}</div>
              <div class="usr-meta">
                <div class="usr-name">{{ u.name }}</div>
                <div class="row-muted">{{ u.phone }}</div>
              </div>
            </div>

            <span class="status-pill" :class="u.status === 'ACTIVE' ? 'success' : 'danger'">
              <i class="bx" :class="u.status === 'ACTIVE' ? 'bx-check-circle' : 'bx-block'"></i>
              {{ u.status === 'ACTIVE' ? "Active" : "Suspended" }}
            </span>
          </div>

          <div class="mid">
            <span class="plan-pill">
              <i class="bx bx-crown"></i>
              {{ planLabel(u.plan) }}
            </span>

            <span class="status-pill" :class="kycClass(u.kyc)">
              <i class="bx" :class="kycIcon(u.kyc)"></i>
              {{ kycLabel(u.kyc) }}
            </span>

            <div class="row-muted">Last Activity: {{ u.lastActivity }}</div>
          </div>

          <div class="card-actions">
            <button class="btn ghost" @click="openDrawer(u)">
              <i class="bx bx-show"></i> View
            </button>

            <button class="btn ghost" @click="toggleStatus(u)">
              <i class="bx" :class="u.status==='ACTIVE'?'bx-block':'bx-check-shield'"></i>
              {{ u.status === "ACTIVE" ? "Suspend" : "Activate" }}
            </button>
          </div>
        </div>

        <div v-if="filtered.length === 0" class="usr-card">
          <div class="empty">
            <i class="bx bx-user-x"></i>
            <div>
              <strong>No users found</strong>
              <div class="row-muted">Try adjusting filters or search query.</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- USER DRAWER -->
    <div v-if="drawer.open" class="drawer-backdrop" @click.self="closeDrawer">
      <aside class="drawer" v-motion-pop>
        <div class="drawer-head">
          <div class="drawer-title">
            <strong>User Profile</strong>
            <span class="row-muted">Internal admin view</span>
          </div>
          <button class="icon-mini" @click="closeDrawer">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="drawer-body">
          <!-- profile card -->
          <div class="profile">
            <div class="profile-top">
              <div class="avatar big">
                {{ (drawer.user?.name || "?").charAt(0).toUpperCase() }}
              </div>
              <div class="pmeta">
                <div class="pname">{{ drawer.user?.name }}</div>
                <div class="row-muted">{{ drawer.user?.email }}</div>
                <div class="row-muted">{{ drawer.user?.phone }}</div>
              </div>
            </div>

            <div class="pills">
              <span class="plan-pill">
                <i class="bx bx-crown"></i>
                {{ planLabel(drawer.user?.plan) }}
              </span>

              <span class="status-pill" :class="kycClass(drawer.user?.kyc)">
                <i class="bx" :class="kycIcon(drawer.user?.kyc)"></i>
                {{ kycLabel(drawer.user?.kyc) }}
              </span>

              <span class="status-pill" :class="drawer.user?.status === 'ACTIVE' ? 'success' : 'danger'">
                <i class="bx" :class="drawer.user?.status === 'ACTIVE' ? 'bx-check-circle' : 'bx-block'"></i>
                {{ drawer.user?.status === 'ACTIVE' ? "Active" : "Suspended" }}
              </span>
            </div>

            <div class="pgrid">
              <div class="kv">
                <span class="k">User ID</span>
                <strong class="v">#{{ drawer.user?.id }}</strong>
              </div>
              <div class="kv">
                <span class="k">City</span>
                <strong class="v">{{ drawer.user?.city }}</strong>
              </div>
              <div class="kv">
                <span class="k">Registered</span>
                <strong class="v">{{ drawer.user?.createdAt }}</strong>
              </div>
              <div class="kv">
                <span class="k">Last Activity</span>
                <strong class="v">{{ drawer.user?.lastActivity }}</strong>
              </div>
            </div>
          </div>

          <!-- quick actions -->
          <div class="section">
            <div class="section-head">
              <strong>Quick Actions</strong>
              <span class="row-muted">Instant operational controls</span>
            </div>

            <div class="qa">
              <!-- <button class="btn ghost" @click="kycApprove(drawer.user)">
                <i class="bx bx-check-circle"></i>
                Approve KYC
              </button>

              <button class="btn ghost" @click="kycReject(drawer.user)">
                <i class="bx bx-x-circle"></i>
                Reject KYC
              </button> -->

              <!-- <button class="btn ghost" @click="toast.info('Open Wallet detail screen next')">
                <i class="bx bx-wallet"></i>
                Wallet
              </button> -->

              <button class="btn ghost" @click="toggleStatus(drawer.user)">
                <i class="bx" :class="drawer.user?.status==='ACTIVE'?'bx-block':'bx-check-shield'"></i>
                {{ drawer.user?.status === "ACTIVE" ? "Suspend" : "Activate" }}
              </button>
            </div>
          </div>

          <!-- internal notes -->
          <div class="section">
            <div class="section-head">
              <strong>Internal Notes</strong>
              <span class="row-muted">For team collaboration</span>
            </div>

            <textarea class="notes" v-model="drawer.notes"
              placeholder="Write internal note about this user..."></textarea>

            <button class="btn primary" @click="saveNotes">
              <i class="bx bx-save"></i>
              Save Note
            </button>
          </div>
        </div>
      </aside>
    </div>
    <!-- ADD USER MODAL -->
    <div v-if="addModal.open" class="modal-backdrop" @click.self="closeAddUserModal">
      <div class="modal" v-motion-pop>
        <div class="modal-head">
          <div>
            <strong>Create New User</strong>
            <div class="row-muted">Add user manually (admin action)</div>
          </div>

          <button class="icon-mini" @click="closeAddUserModal" title="Close">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="form-grid">
            <div class="field">
              <label>Full Name</label>
              <div class="input">
                <i class="bx bx-user"></i>
                <input v-model="newUser.name" placeholder="Rahul Sharma" />
              </div>
            </div>

            <div class="field">
              <label>Email</label>
              <div class="input">
                <i class="bx bx-envelope"></i>
                <input v-model="newUser.email" placeholder="rahul@gmail.com" />
              </div>
            </div>

            <div class="field">
              <label>Phone</label>
              <div class="input">
                <i class="bx bx-phone"></i>
                <input v-model="newUser.phone" placeholder="+91 98xxxxxxx" />
              </div>
            </div>

            <div class="field">
              <label>City</label>
              <div class="input">
                <i class="bx bx-map"></i>
                <input v-model="newUser.city" placeholder="Delhi" />
              </div>
            </div>

            <div class="field">
              <label>Plan</label>
              <div class="input">
                <i class="bx bx-crown"></i>
                <select class="select full" v-model="newUser.plan">
                  <option v-for="p in plans" :key="p.value" :value="p.value">
                    {{ p.label }}
                  </option>
                </select>
              </div>
            </div>

            <div class="field">
              <label>Status</label>
              <div class="input">
                <i class="bx bx-check-shield"></i>
                <select class="select full" v-model="newUser.status">
                  <option value="ACTIVE">Active</option>
                  <option value="SUSPENDED">Suspended</option>
                </select>
              </div>
            </div>

            <div class="field">
              <label>KYC Status</label>
              <div class="input">
                <i class="bx bx-id-card"></i>
                <select class="select full" v-model="newUser.kyc">
                  <option value="NOT_STARTED">Not Started</option>
                  <option value="PENDING">Pending</option>
                  <option value="VERIFIED">Verified</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>
            </div>

            <div class="field">
              <label>Initial Note (optional)</label>
              <div class="input">
                <i class="bx bx-note"></i>
                <input v-model="newUser.note" placeholder="Example: created by admin..." />
              </div>
            </div>
          </div>

          <div class="divider"></div>

          <!-- ✅ UI hint -->
          <div class="mini tip">
            <i class="bx bx-info-circle"></i>
            User will be created instantly. You can update KYC, plan, and access anytime.
          </div>
        </div>

        <div class="modal-foot">
          <button class="btn ghost" @click="closeAddUserModal">
            Cancel
          </button>

          <button class="btn primary" :disabled="addModal.creating" @click="createUser">
            <span v-if="addModal.creating" class="loader" style="margin-right:8px;"></span>
            <i v-else class="bx bx-user-plus"></i>
            {{ addModal.creating ? "Creating..." : "Create User" }}
          </button>
        </div>
      </div>
    </div>
    <div v-if="contactModal" class="modal-overlay">
      <div class="modal-box">

        <div class="modal-head">
          <h3>Customer Contacts</h3>
          <button class="btn ghost" @click="contactModal=false">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="modal-body">

          <div class="search-mini">
            <i class="bx bx-search"></i>
            <input v-model="contactSearch" @input="loadContacts(0)" placeholder="Search contact..." />
          </div>

          <table class="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Date</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="c in contacts" :key="c.user_contact_id">
                <td>{{ c.user_contact_name }}</td>
                <td>{{ c.user_contact_phone }}</td>
                <td>{{ c.user_contact_datetime }}</td>
              </tr>

              <tr v-if="contacts.length===0">
                <td colspan="3">No contacts found</td>
              </tr>
            </tbody>
          </table>

          <!-- Pagination -->
          <div class="pagination" style="margin-top: 2rem;">
            <button class="btn ghost" :disabled="contactPage==0" @click="loadContacts(contactPage-1)">
              Prev
            </button>

            <span style="margin-left: 1rem; margin-right: 1rem;">{{ contactPage+1 }}</span>

            <button class="btn ghost" :disabled="contacts.length<10" @click="loadContacts(contactPage+1)">
              Next
            </button>
          </div>

        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, reactive, onMounted, watch } from "vue";
  import { useToast } from "vue-toastification";

  import {
    getPlans,
    getCustomers,
    addCustomer,
    updateCustomerStatus,
    deleteCustomer,
    saveCustomerNote,
    exportCustomersXl,
    getCustomerContacts
  } from "@/apis/cuscon";

  const toast = useToast();



  /* ===========================
     ADMIN SESSION
     =========================== */
  const ldata = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("ldata") || "{}");
    } catch (e) {
      return {};
    }
  })();
  const adminId = ldata?.user_id || ldata?.email || "system";


  const exportNow = async () => {
    try {
      const blob = await exportCustomersXl({
        keyword: query.value,
        statusFilter: statusFilter.value,
        kycFilter: kycFilter.value,
        planFilter: planFilter.value,
      });

      if (!blob) {
        toast.error("Export failed");
        return;
      }

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `customers_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Export downloaded ✅");
    } catch (e) {
      toast.error("Export failed");
    }
  };


  /* ===========================
     DATA
     =========================== */
  const plans = ref([]);      // dropdown data from API
  const users = ref([]);      // list from API

  const loadingList = ref(false);
  const total = ref(0);
  const page_no = ref(0);

  /* filters */
  const query = ref("");
  const statusFilter = ref("");
  const kycFilter = ref("");
  const planFilter = ref("");

  const contactModal = ref(false);
  const contacts = ref([]);
  const contactPage = ref(0);
  const contactTotal = ref(0);
  const contactSearch = ref("");
  const selectedUser = ref(null);

  const openContacts = async (user_id) => {
    selectedUser.value = user_id;
    contactModal.value = true;
    loadContacts(0);
  }

  const loadContacts = async (page = 0) => {

    contactPage.value = page;

    const res = await getCustomerContacts({
      user_id: selectedUser.value,
      page_no: page,
      search_key: contactSearch.value
    });

    if (res.status) {
      contacts.value = res.records;
      contactTotal.value = res.total;
    }

  }

  /* actions loader */
  const actionLoading = reactive({
    user_id: "",
    type: "", // status/delete
  });

  /* ===========================
     MAPPERS (DB <-> UI)
     =========================== */
  const statusFromDb = (db) => (db === "active" ? "ACTIVE" : "SUSPENDED");
  // const statusToDb = (ui) => (ui === "ACTIVE" ? "active" : "suspended");

  /* ===========================
     COMPUTED UI FILTERING
     (API already filters, still safe)
     =========================== */
  const filtered = computed(() =>
    users.value.filter((u) => {
      const q = (query.value || "").toLowerCase();

      const matchesQ =
        !q ||
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.phone || "").toLowerCase().includes(q);

      const matchesStatus = !statusFilter.value || u.status === statusFilter.value;
      const matchesKyc = !kycFilter.value || u.kyc === kycFilter.value;
      const matchesPlan = !planFilter.value || u.plan === planFilter.value;

      return matchesQ && matchesStatus && matchesKyc && matchesPlan;
    })
  );

  const kycPending = computed(() => users.value.filter((u) => u.kyc === "PENDING").length);
  const suspended = computed(() => users.value.filter((u) => u.status === "SUSPENDED").length);
  const activeToday = computed(() => users.value.filter((u) => (u.lastActivity || "").includes("Today")).length);

  /* helpers */
  const planLabel = (code) => plans.value.find((p) => p.value === code)?.label || code || "-";

  const kycLabel = (k) => {
    if (!k) return "-";
    return (
      {
        VERIFIED: "Verified",
        PENDING: "Pending",
        REJECTED: "Rejected",
        NOT_STARTED: "Not Started",
      }[k] || k
    );
  };

  const kycClass = (k) => {
    if (k === "VERIFIED") return "success";
    if (k === "PENDING") return "warning";
    if (k === "REJECTED") return "danger";
    return "primary";
  };

  const kycIcon = (k) => {
    if (k === "VERIFIED") return "bx-check-circle";
    if (k === "PENDING") return "bx-time-five";
    if (k === "REJECTED") return "bx-x-circle";
    return "bx-info-circle";
  };

  /* ===========================
     DRAWER
     =========================== */
  const drawer = reactive({
    open: false,
    user: null,
    notes: "",
    savingNote: false,
    loadingKyc: false,
    kycAction: "",
    loadingStatus: false,
  });

  const openDrawer = (user) => {
    drawer.open = true;
    drawer.user = user;
    drawer.notes = "";
  };

  const closeDrawer = () => {
    drawer.open = false;
    drawer.user = null;
  };

  /* ===========================
     ADD USER MODAL
     =========================== */
  const addModal = reactive({
    open: false,
    creating: false,
  });

  const newUser = reactive({
    name: "",
    email: "",
    phone: "",
    city: "",
    plan: "FREE",
    status: "ACTIVE",
    kyc: "NOT_STARTED",
    note: "",
  });

  const openAddUserModal = () => {
    addModal.open = true;

    Object.assign(newUser, {
      name: "",
      email: "",
      phone: "",
      city: "",
      plan: "FREE",
      status: "ACTIVE",
      kyc: "NOT_STARTED",
      note: "",
    });
  };

  const closeAddUserModal = () => {
    addModal.open = false;
  };

  const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

  /* ===========================
     API LOADERS
     =========================== */
  const loadPlans = async () => {
    const res = await getPlans();
    if (res.status) {
      // Convert DB plan rows -> dropdown structure UI needs
      const arr = (res.plans || []).map((p) => ({
        value: p.mplan_code || "FREE",
        label: p.mplan_name || p.mplan_code || "Plan",
      }));

      // fallback if no plan exists
      plans.value = arr.length
        ? arr
        : [
          { value: "FREE", label: "Free" },
          { value: "BASIC", label: "Basic" },
          { value: "PREMIUM", label: "Premium" },
          { value: "VIP", label: "VIP" },
        ];
    } else {
      plans.value = [
        { value: "FREE", label: "Free" },
        { value: "BASIC", label: "Basic" },
        { value: "PREMIUM", label: "Premium" },
        { value: "VIP", label: "VIP" },
      ];
    }
  };

  const loadCustomers = async () => {
    loadingList.value = true;

    try {
      const res = await getCustomers({
        page_no: page_no.value,
        keyword: query.value,
        statusFilter: statusFilter.value,
        kycFilter: kycFilter.value,
        planFilter: planFilter.value,
      });

      if (!res.status) {
        toast.error(res.success || "Failed to fetch users");
        users.value = [];
        total.value = 0;
        return;
      }

      const records = res.records || [];
      total.value = res.total || 0;

      // map DB -> UI structure (same shape as your demo list)
      users.value = records.map((u) => {
        const name = `${u.user_fname || ""} ${u.user_lname || ""}`.trim() || "User";

        return {
          id: u.user_id,
          name: name,
          email: u.user_email || "-",
          phone: u.user_mobile || "-",
          city: "-", // no column in user_tbl, keep "-"
          plan: u.plan_code || "FREE",
          kyc: u.kyc_status || "NOT_STARTED",
          status: statusFromDb(u.user_status),
          lastActivity: u.last_activity || "-",
          createdAt: u.user_datetime || "-",
        };
      });

    } catch (e) {
      toast.error("Request Failed");
    } finally {
      loadingList.value = false;
    }
  };

  /* ===========================
     ACTIONS
     =========================== */
  const createUser = async () => {
    try {
      const name = (newUser.name || "").trim();
      const email = (newUser.email || "").trim();
      const phone = (newUser.phone || "").trim();

      if (!name || name.length < 3) return toast.error("Enter valid full name");
      if (!email || !isEmail(email)) return toast.error("Enter valid email");
      if (!phone || phone.length < 8) return toast.error("Enter valid phone number");

      addModal.creating = true;

      const res = await addCustomer({
        name,
        email,
        phone,
        city: (newUser.city || "").trim(),
        plan_code: newUser.plan,
        status: newUser.status,
        kyc: newUser.kyc,
        note: (newUser.note || "").trim(),
        created_by: adminId,
      });

      if (!res.status) {
        toast.error(res.success || "Failed to create user");
        return;
      }

      toast.success("User created successfully ✅");
      addModal.open = false;

      // refresh list
      await loadCustomers();

    } catch (e) {
      toast.error("Failed to create user");
    } finally {
      addModal.creating = false;
    }
  };

  const toggleStatus = async (u) => {
    if (!u) return;

    const next = u.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE";

    try {
      actionLoading.user_id = u.id;
      actionLoading.type = "status";

      const res = await updateCustomerStatus(u.id, next, adminId);

      if (!res.status) {
        toast.error(res.success || "Failed to update status");
        return;
      }

      u.status = next;
      toast.success(`User ${next === "ACTIVE" ? "activated" : "suspended"} ✅`);
    } catch (e) {
      toast.error("Request Failed");
    } finally {
      actionLoading.user_id = "";
      actionLoading.type = "";
    }
  };

  const remove = async (u) => {
    if (!confirm(`Delete user: ${u.name}?`)) return;

    try {
      actionLoading.user_id = u.id;
      actionLoading.type = "delete";

      const res = await deleteCustomer(u.id);

      if (!res.status) {
        toast.error(res.success || "Failed to delete user");
        return;
      }

      users.value = users.value.filter((x) => x.id !== u.id);
      toast.success("User deleted ✅");
    } catch (e) {
      toast.error("Request Failed");
    } finally {
      actionLoading.user_id = "";
      actionLoading.type = "";
    }
  };

  // const kycApprove = async (u) => {
  //   if (!u) return;

  //   try {
  //     drawer.loadingKyc = true;
  //     drawer.kycAction = "approve";

  //     const res = await updateCustomerKyc(u.id, "VERIFIED", adminId);
  //     if (!res.status) {
  //       toast.error(res.success || "Failed to approve KYC");
  //       return;
  //     }

  //     u.kyc = "VERIFIED";
  //     toast.success("KYC approved ✅");
  //   } catch (e) {
  //     toast.error("Request Failed");
  //   } finally {
  //     drawer.loadingKyc = false;
  //     drawer.kycAction = "";
  //   }
  // };

  // const kycReject = async (u) => {
  //   if (!u) return;

  //   try {
  //     drawer.loadingKyc = true;
  //     drawer.kycAction = "reject";

  //     const res = await updateCustomerKyc(u.id, "REJECTED", adminId);
  //     if (!res.status) {
  //       toast.error(res.success || "Failed to reject KYC");
  //       return;
  //     }

  //     u.kyc = "REJECTED";
  //     toast.error("KYC rejected");
  //   } catch (e) {
  //     toast.error("Request Failed");
  //   } finally {
  //     drawer.loadingKyc = false;
  //     drawer.kycAction = "";
  //   }
  // };

  const saveNotes = async () => {
    if (!drawer.user) return;

    const note = (drawer.notes || "").trim();
    if (!note) return toast.error("Write note first");

    try {
      drawer.savingNote = true;

      const res = await saveCustomerNote(drawer.user.id, note, adminId);

      if (!res.status) {
        toast.error(res.success || "Failed to save note");
        return;
      }

      toast.success("Note saved ✅");
      drawer.notes = "";
    } catch (e) {
      toast.error("Request Failed");
    } finally {
      drawer.savingNote = false;
    }
  };

  /* ===========================
     INIT
     =========================== */
  onMounted(async () => {
    await loadPlans();
    await loadCustomers();
  });

  /* ✅ auto reload list on filters */
  watch([statusFilter, kycFilter, planFilter], async () => {
    page_no.value = 0;
    await loadCustomers();
  });
</script>


<style scoped>
  .actions {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
  }

  .search-mini {
    height: 44px;
    position: relative;
    min-width: 260px;
  }

  .search-mini i {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(148, 163, 184, 0.92);
    font-size: 18px;
  }

  .search-mini input {
    width: 100%;
    height: 44px;
    border-radius: 16px;
    border: 1px solid var(--line);
    padding: 0 12px 0 42px;
    background: rgba(255, 255, 255, 0.95);
    font-weight: var(--w-medium);
    font-size: 13px;
    outline: none;
  }

  .search-mini input:focus {
    border-color: rgba(46, 109, 231, 0.35);
    box-shadow: 0 0 0 4px rgba(46, 109, 231, 0.12);
  }

  .select {
    height: 44px;
    border-radius: 16px;
    border: 1px solid var(--line);
    padding: 0 12px;
    background: rgba(255, 255, 255, 0.95);
    font-weight: var(--w-medium);
    font-size: 13px;
    outline: none;
  }

  .table-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
    margin-bottom: 12px;
  }

  .table-title {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .table-title strong {
    font-weight: var(--w-bold);
  }

  .muted {
    color: var(--muted);
    font-size: 12px;
    font-weight: var(--w-medium);
  }

  .usr {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .usr-meta {
    display: flex;
    flex-direction: column;
  }

  .usr-name {
    font-weight: var(--w-bold);
    font-size: 13px;
  }

  .plan-pill {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    padding: 7px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: var(--w-semibold);
    border: 1px solid var(--line);
    background: rgba(241, 245, 249, 0.65);
  }

  .plan-pill i {
    font-size: 16px;
    color: rgba(100, 116, 139, 0.85);
  }

  .row-actions {
    display: inline-flex;
    gap: 8px;
    justify-content: flex-end;
  }

  .icon-mini {
    height: 38px;
    width: 38px;
    border-radius: 14px;
    border: 1px solid var(--line);
    background: rgba(255, 255, 255, 0.96);
    display: grid;
    place-items: center;
    cursor: pointer;
  }

  .icon-mini i {
    font-size: 18px;
    color: rgba(11, 18, 32, 0.88);
  }

  .icon-mini:hover {
    transform: translateY(-1px);
  }

  .icon-mini.danger {
    background: rgba(239, 68, 68, 0.08);
    border-color: rgba(239, 68, 68, 0.18);
  }

  .icon-mini.danger i {
    color: var(--danger);
  }

  .empty {
    display: flex;
    gap: 12px;
    align-items: center;
    padding: 6px 2px;
  }

  .empty i {
    font-size: 26px;
    color: rgba(148, 163, 184, 0.9);
  }

  .empty strong {
    font-weight: var(--w-bold);
  }

  /* mobile cards */
  .list-cards {
    display: none;
    margin-top: 10px;
    gap: 12px;
  }

  .usr-card {
    border: 1px solid var(--line);
    border-radius: var(--radius-xl);
    padding: 14px;
    background: rgba(255, 255, 255, 0.95);
    box-shadow: var(--shadow-sm);
  }

  .usr-card .top {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: center;
  }

  .usr-card .mid {
    margin-top: 10px;
    display: grid;
    gap: 8px;
  }

  .card-actions {
    margin-top: 12px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  /* drawer */
  .drawer-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(2, 6, 23, 0.55);
    backdrop-filter: blur(8px);
    z-index: 90;
    display: flex;
    justify-content: flex-end;
  }

  .drawer {
    width: min(520px, 92vw);
    height: 100%;
    background: rgba(255, 255, 255, 0.95);
    border-left: 1px solid var(--line);
    box-shadow: var(--shadow);
    padding: 16px;
    overflow: auto;
  }

  .drawer-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--line);
  }

  .drawer-title strong {
    font-weight: var(--w-bold);
    font-size: 16px;
  }

  .drawer-body {
    margin-top: 14px;
    display: grid;
    gap: 14px;
  }

  .profile {
    border: 1px solid var(--line);
    background: rgba(246, 248, 252, 0.65);
    border-radius: var(--radius-xl);
    padding: 14px;
  }

  .profile-top {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  .avatar.big {
    height: 56px;
    width: 56px;
    font-size: 16px;
  }

  .pname {
    font-weight: var(--w-black);
    letter-spacing: -0.2px;
  }

  .pills {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 12px;
  }

  .pgrid {
    margin-top: 12px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .kv {
    border: 1px solid var(--line);
    background: rgba(255, 255, 255, 0.94);
    border-radius: 18px;
    padding: 10px;
  }

  .kv .k {
    font-size: 11px;
    color: var(--muted);
    font-weight: var(--w-medium);
  }

  .kv .v {
    margin-top: 5px;
    display: block;
    font-size: 13px;
    font-weight: var(--w-bold);
  }

  .section {
    border: 1px solid var(--line);
    border-radius: var(--radius-xl);
    padding: 14px;
    background: rgba(255, 255, 255, 0.95);
  }

  .section-head {
    display: grid;
    gap: 4px;
    margin-bottom: 12px;
  }

  .section-head strong {
    font-weight: var(--w-bold);
  }

  .qa {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .notes {
    width: 100%;
    min-height: 110px;
    border-radius: 18px;
    border: 1px solid var(--line);
    background: rgba(255, 255, 255, 0.96);
    padding: 12px 12px;
    outline: none;
    font-size: 13px;
    font-weight: var(--w-medium);
    resize: vertical;
    margin-bottom: 10px;
  }

  .notes:focus {
    border-color: rgba(46, 109, 231, 0.35);
    box-shadow: 0 0 0 4px rgba(46, 109, 231, 0.12);
  }

  @media (max-width: 960px) {
    .desktop-only {
      display: none;
    }

    .mobile-only {
      display: block;
    }

    .list-cards {
      display: grid;
    }

    .pgrid {
      grid-template-columns: 1fr;
    }
  }

  /* =========================
   Modal (Add User)
   ========================= */
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(2, 6, 23, 0.55);
    backdrop-filter: blur(10px);
    z-index: 95;
    display: grid;
    place-items: center;
    padding: 16px;
  }

  .modal {
    width: min(780px, 96vw);
    border-radius: 26px;
    border: 1px solid rgba(231, 238, 248, 0.9);
    background: rgba(255, 255, 255, 0.96);
    box-shadow: var(--shadow);
    overflow: hidden;
  }

  .modal-head {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: flex-start;
    padding: 16px 16px 12px;
    border-bottom: 1px solid var(--line);
    background: rgba(246, 248, 252, 0.65);
  }

  .modal-head strong {
    font-weight: var(--w-black);
    letter-spacing: -0.2px;
    font-size: 16px;
  }

  .modal-body {
    padding: 16px;
  }

  .modal-foot {
    padding: 12px 16px 16px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    flex-wrap: wrap;
    border-top: 1px solid var(--line);
    background: rgba(246, 248, 252, 0.55);
  }

  .modal .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  @media (max-width: 760px) {
    .modal .form-grid {
      grid-template-columns: 1fr;
    }
  }

  /* ✅ Select icon overlap fix (popup + all selects) */
  .input select.select.full {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;

    padding-left: 44px;
    /* left icon space */
    padding-right: 44px;
    /* ✅ right arrow space */
    cursor: pointer;
  }

  /* ✅ custom arrow icon */
  .input .dd {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    font-size: 18px;
    color: rgba(148, 163, 184, 0.95);
  }

  /* ===========================================
   ✅ USERS SCREEN MOBILE PATCH (END ONLY)
   Desktop CSS unchanged
   =========================================== */

  @media (max-width: 980px) {

    /* header stack */
    .page-head {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 12px !important;
    }

    /* actions -> vertical full width */
    .actions {
      width: 100% !important;
      flex-direction: column !important;
      align-items: stretch !important;
      gap: 10px !important;
    }

    .search-mini {
      width: 100% !important;
      min-width: 0 !important;
    }

    .search-mini input {
      width: 100% !important;
    }

    .select {
      width: 100% !important;
    }

    /* export button full */
    .actions .btn.ghost {
      width: 100% !important;
      justify-content: center !important;
      height: 48px !important;
      border-radius: 18px !important;
    }

    /* stats -> 2 columns */
    .grid.cols-4 {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 12px !important;
    }

    /* table head stack */
    .table-head {
      flex-direction: column !important;
      align-items: stretch !important;
      gap: 10px !important;
    }

    .table-head .btn.primary {
      width: 100% !important;
      justify-content: center !important;
      height: 48px !important;
      border-radius: 18px !important;
    }

    /* mobile cards actions full width buttons */
    .card-actions .btn.ghost {
      flex: 1 1 100% !important;
      width: 100% !important;
      justify-content: center !important;
    }

    /* drawer full width on phone (no right overflow) */
    .drawer-backdrop {
      justify-content: center !important;
      padding: 0 !important;
    }

    .drawer {
      width: 100% !important;
      max-width: 100% !important;
      border-radius: 0 !important;
      border-left: none !important;
      padding-bottom: 24px !important;
    }

    /* drawer grids */
    .pgrid {
      grid-template-columns: 1fr !important;
    }

    .qa .btn.ghost {
      width: 100% !important;
      justify-content: center !important;
    }

    /* ===================
     ✅ Add User Modal
     =================== */
    .modal-backdrop {
      padding: 12px !important;
    }

    .modal {
      width: 100% !important;
      max-width: 560px !important;
      border-radius: 22px !important;
    }

    .modal-body {
      padding: 14px !important;
    }

    .modal .form-grid {
      grid-template-columns: 1fr !important;
    }

    .modal-foot {
      flex-direction: column-reverse !important;
      align-items: stretch !important;
    }

    .modal-foot .btn {
      width: 100% !important;
      height: 48px !important;
      justify-content: center !important;
      border-radius: 18px !important;
    }

    /* ✅ important: make all input/select full width in modal */
    .modal .field,
    .modal .input,
    .modal .select.full,
    .modal input {
      width: 100% !important;
    }
  }

  /* ✅ extra small phones */
  @media (max-width: 520px) {
    .grid.cols-4 {
      grid-template-columns: 1fr !important;
    }

    .usr-card .top {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 10px !important;
    }

    .status-pill {
      width: fit-content !important;
    }

    .modal-head {
      padding: 14px !important;
    }
  }

  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, .4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-box {
    background: #fff;
    width: 700px;
    max-height: 80vh;
    overflow: auto;
    border-radius: 10px;
    padding: 20px;
  }

  .modal-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
  }

  /* ===== UsersScreen UI Fix Patch ===== */

  .actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
  }

  /* search */
  .search-mini {
    display: flex;
    align-items: center;
    gap: 6px;
    background: #f6f7fb;
    border-radius: 8px;
    padding: 6px 10px;
  }

  .search-mini input {
    border: none;
    outline: none;
    background: transparent;
    min-width: 180px;
    width: 100%;
  }

  /* table overflow */
  .table-wrap {
    width: 100%;
    overflow-x: auto;
  }

  /* user avatar row */
  .usr {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .avatar {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #4f46e5;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
  }

  .avatar.big {
    width: 56px;
    height: 56px;
    font-size: 20px;
  }

  /* row actions */
  .row-actions {
    display: flex;
    gap: 6px;
    justify-content: flex-end;
  }

  /* drawer */
  .drawer {
    width: 420px;
    max-width: 90vw;
    height: 100%;
    background: #fff;
    overflow: auto;
    border-left: 1px solid #eee;
  }

  /* modal */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, .4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal {
    width: 600px;
    max-width: 92vw;
    background: #fff;
    border-radius: 10px;
    overflow: hidden;
  }

  .modal-body {
    padding: 20px;
    max-height: 70vh;
    overflow: auto;
  }

  .modal-foot {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 15px;
    border-top: 1px solid #eee;
  }

  /* form grid */
  .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  /* input */
  .input {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #f6f7fb;
    padding: 8px 10px;
    border-radius: 8px;
  }

  .input input,
  .input select {
    border: none;
    outline: none;
    background: transparent;
    width: 100%;
  }

  /* responsive */
  .desktop-only {
    display: block;
  }

  .mobile-only {
    display: none;
  }

  @media (max-width:900px) {

    .desktop-only {
      display: none;
    }

    .mobile-only {
      display: block;
    }

    .form-grid {
      grid-template-columns: 1fr;
    }

    .actions {
      justify-content: flex-start;
    }

    .drawer {
      width: 100%;
    }

  }
</style>